import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown, Leaf, Palette, Axe, Lightbulb, User } from 'lucide-react';
import { Button } from '@/components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_pms-provence-garden/artifacts/vi5mnxzf_jardily.png";

// French flag component
const FrenchFlag = () => (
  <svg width="20" height="14" viewBox="0 0 20 14" className="rounded-sm shadow-sm">
    <rect x="0" y="0" width="6.67" height="14" fill="#002395"/>
    <rect x="6.67" y="0" width="6.67" height="14" fill="#FFFFFF"/>
    <rect x="13.33" y="0" width="6.67" height="14" fill="#ED2939"/>
  </svg>
);

// Booking options for the reservation modal
const bookingOptions = [
  {
    id: 'entretien',
    title: 'Entretien de jardin',
    subtitle: 'Avance immédiate crédit d\'impôts (-50%)',
    description: 'Tonte, taille, désherbage, entretien régulier',
    icon: Leaf,
    iconColor: 'text-emerald-600',
    bgColor: 'bg-emerald-50 hover:bg-emerald-100',
    borderColor: 'border-emerald-200',
    href: '/entretien',
    hasTaxCredit: true
  },
  {
    id: 'amenagement',
    title: 'Aménagement paysager',
    description: 'Création de jardins, terrasses, allées',
    icon: Palette,
    iconColor: 'text-purple-600',
    bgColor: 'bg-purple-50 hover:bg-purple-100',
    borderColor: 'border-purple-200',
    href: '/amenagement',
    hasTaxCredit: false
  },
  {
    id: 'elagage',
    title: 'Élagage',
    description: 'Taille et abattage d\'arbres',
    icon: Axe,
    iconColor: 'text-amber-600',
    bgColor: 'bg-amber-50 hover:bg-amber-100',
    borderColor: 'border-amber-200',
    href: '/elagage',
    hasTaxCredit: false
  },
  {
    id: 'conseil',
    title: 'Conseils & Études',
    description: 'Études paysagères et conseils personnalisés',
    icon: Lightbulb,
    iconColor: 'text-blue-600',
    bgColor: 'bg-blue-50 hover:bg-blue-100',
    borderColor: 'border-blue-200',
    href: '/conseil',
    hasTaxCredit: false
  }
];

const Header = ({ transparent = false }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isReservationOpen, setIsReservationOpen] = useState(false);
  const location = useLocation();
  
  const isHome = location.pathname === '/';
  const shouldBeTransparent = transparent || isHome;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Listen for custom event to open reservation modal
  useEffect(() => {
    const handleOpenModal = () => setIsReservationOpen(true);
    window.addEventListener('openReservationModal', handleOpenModal);
    return () => window.removeEventListener('openReservationModal', handleOpenModal);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Accueil', href: '/' },
    { name: 'Services', href: '/services' },
    { name: 'Blog', href: '/blog' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href.replace('/#', ''));
  };

  const headerBg = shouldBeTransparent && !isScrolled 
    ? 'bg-transparent' 
    : 'bg-white/95 backdrop-blur-md shadow-sm';
  
  const textColor = shouldBeTransparent && !isScrolled 
    ? 'text-white' 
    : 'text-stone-700';
  
  const linkHoverColor = shouldBeTransparent && !isScrolled 
    ? 'hover:text-emerald-300' 
    : 'hover:text-emerald-600';

  return (
    <>
      <header 
        className={`fixed top-0 w-full z-50 transition-all duration-500 ${headerBg}`}
        data-testid="header"
      >
        {/* Main nav */}
        <nav className="container-custom py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <img 
                src={LOGO_URL} 
                alt="Jardily" 
                className="h-10 md:h-11 lg:h-14 w-auto drop-shadow-lg"
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`text-sm font-medium uppercase tracking-wide transition-colors ${textColor} ${linkHoverColor} ${
                    isActive(link.href) ? 'text-emerald-400' : ''
                  }`}
                  data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Contact & CTA */}
            <div className="hidden lg:flex items-center gap-4">
              <div className="flex flex-col items-end">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <FrenchFlag />
                  <span className={`text-xs font-medium ${shouldBeTransparent && !isScrolled ? 'text-white/70' : 'text-stone-500'}`}>
                    Service client
                  </span>
                </div>
                <a 
                  href="tel:0465011022" 
                  className={`flex items-center gap-2 text-sm font-semibold transition-colors ${textColor} ${linkHoverColor}`}
                >
                  <Phone className="w-4 h-4" />
                  04 65 01 10 22
                </a>
              </div>
              
              {/* Espace client button */}
              <a 
                href="https://resawork.fr/connexion/jardily"
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all ${
                  shouldBeTransparent && !isScrolled 
                    ? 'border-white/30 text-white hover:bg-white/10' 
                    : 'border-stone-300 text-stone-700 hover:bg-stone-100'
                }`}
                data-testid="client-area-btn"
              >
                <User className="w-4 h-4" />
                <span className="text-sm font-medium">Espace client</span>
              </a>
              
              <Button 
                onClick={() => setIsReservationOpen(true)}
                className="bg-emerald-500 hover:bg-emerald-400 text-white rounded-full px-6 py-2 font-semibold shadow-lg shadow-emerald-500/30 hover:shadow-emerald-400/40 transition-all flex items-center gap-2"
                data-testid="reservation-btn"
              >
                Réserver
                <ChevronDown className="w-4 h-4" />
              </Button>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 ${textColor}`}
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Nav */}
          {isMobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 border-t border-white/20 pt-4 animate-fade-in bg-black/80 backdrop-blur-md rounded-2xl p-4 -mx-4">
              <div className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="text-base font-medium text-white hover:text-emerald-400 transition-colors"
                    data-testid={`mobile-nav-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.name}
                  </Link>
                ))}
                <div className="flex flex-col gap-2 mt-2">
                  <a 
                    href="https://resawork.fr/connexion/jardily"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 py-2.5 border border-white/30 text-white rounded-full hover:bg-white/10 transition-colors"
                    data-testid="mobile-client-area-btn"
                  >
                    <User className="w-4 h-4" />
                    Espace client
                  </a>
                  <Button 
                    onClick={() => { setIsMobileMenuOpen(false); setIsReservationOpen(true); }}
                    className="w-full bg-emerald-500 hover:bg-emerald-400 text-white rounded-full"
                  >
                    Réserver
                  </Button>
                </div>
                <div className="mt-4 pt-4 border-t border-white/20">
                  <div className="flex items-center gap-2 mb-2">
                    <FrenchFlag />
                    <span className="text-white/70 text-xs">Service client</span>
                  </div>
                  <a href="tel:0465011022" className="flex items-center gap-2 text-white font-semibold">
                    <Phone className="w-4 h-4" />
                    04 65 01 10 22
                  </a>
                </div>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Reservation Modal - 4 Options */}
      {isReservationOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setIsReservationOpen(false)}
            data-testid="reservation-backdrop"
          />
          
          {/* Modal */}
          <div className="relative z-10 bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden" data-testid="reservation-modal">
            {/* Header with gradient extending to first option */}
            <div className="bg-gradient-to-b from-emerald-500 to-emerald-400 px-6 pt-6 pb-28 text-white relative">
              {/* Close button */}
              <button 
                onClick={() => setIsReservationOpen(false)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                data-testid="close-reservation-modal"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              
              <h3 className="text-2xl font-bold text-center">Comment pouvons-nous vous aider ?</h3>
              <p className="text-emerald-100 text-center mt-2">Choisissez le service qui correspond à vos besoins</p>
            </div>
            
            {/* Options container - overlaps the header */}
            <div className="px-5 -mt-20 pb-6 space-y-4">
              {/* Entretien - Special first option with border */}
              <Link
                to="/entretien"
                onClick={() => setIsReservationOpen(false)}
                className="block relative bg-white rounded-2xl border-2 border-emerald-400 p-4 shadow-lg hover:shadow-xl transition-all group"
                data-testid="booking-option-entretien"
              >
                {/* Badge */}
                <div className="absolute -top-3 right-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-md">
                  <div className="text-center leading-tight">
                    <span className="block">Avance immédiate (-50%)</span>
                    <span className="block text-[10px] font-normal opacity-90">crédit d'impôts</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-emerald-500 text-white shadow-md">
                    <Leaf className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <span className="font-bold text-emerald-700 text-lg">Entretien de jardin</span>
                    <p className="text-emerald-600 text-sm">Tonte, taille, désherbage, débroussaillage</p>
                  </div>
                  <ChevronDown className="w-5 h-5 text-emerald-500 -rotate-90 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>

              {/* Aménagement */}
              <Link
                to="/amenagement"
                onClick={() => setIsReservationOpen(false)}
                className="block bg-white rounded-2xl border border-stone-200 p-4 shadow-sm hover:shadow-md hover:border-purple-300 transition-all group"
                data-testid="booking-option-amenagement"
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-purple-100 text-purple-600">
                    <Palette className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <span className="font-bold text-stone-800">Aménagement paysager</span>
                    <p className="text-stone-500 text-sm">Création de jardins, terrasses, allées</p>
                  </div>
                  <ChevronDown className="w-5 h-5 text-stone-400 -rotate-90 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>

              {/* Élagage */}
              <Link
                to="/elagage"
                onClick={() => setIsReservationOpen(false)}
                className="block bg-white rounded-2xl border border-stone-200 p-4 shadow-sm hover:shadow-md hover:border-amber-300 transition-all group"
                data-testid="booking-option-elagage"
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-amber-100 text-amber-600">
                    <Axe className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <span className="font-bold text-stone-800">Élagage</span>
                    <p className="text-stone-500 text-sm">Taille et abattage d'arbres</p>
                  </div>
                  <ChevronDown className="w-5 h-5 text-stone-400 -rotate-90 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>

              {/* Conseils */}
              <Link
                to="/conseil"
                onClick={() => setIsReservationOpen(false)}
                className="block bg-white rounded-2xl border border-stone-200 p-4 shadow-sm hover:shadow-md hover:border-blue-300 transition-all group"
                data-testid="booking-option-conseil"
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-blue-100 text-blue-600">
                    <Lightbulb className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <span className="font-bold text-stone-800">Conseils & Études</span>
                    <p className="text-stone-500 text-sm">Diagnostic, plans et accompagnement</p>
                  </div>
                  <ChevronDown className="w-5 h-5 text-stone-400 -rotate-90 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </div>
            
            {/* Footer with phone */}
            <div className="px-6 py-5 bg-stone-50 border-t border-stone-100 text-center">
              <p className="text-stone-500 text-sm mb-1">Besoin d'aide ?</p>
              <a href="tel:0465011022" className="inline-flex items-center gap-2 text-emerald-600 font-semibold text-lg hover:text-emerald-700 transition-colors">
                <Phone className="w-5 h-5" />
                04 65 01 10 22
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
